import { useCallback, useMemo, useState } from "react";
import { TreeIndex } from "./treeIndex";

export function useTreeSelection(index: TreeIndex) {
  const [selected, setSelected] = useState<string[]>([]);

  const getDescendants = useCallback(
    (id: string): string[] => {
      const node = index.nodes[id];
      if (!node) return [];

      const result: string[] = [];

      for (const child of node.children ?? []) {
        result.push(child);
        result.push(...getDescendants(child));
      }

      return result;
    },
    [index]
  );

  const getAncestors = useCallback(
    (id: string): string[] => {
      const result: string[] = [];
      let current = index.nodes[id];

      while (current?.parent) {
        result.push(current.parent);
        current = index.nodes[current.parent];
      }

      return result;
    },
    [index]
  );

  const toggleNode = useCallback(
    (id: string) => {
      const descendants = getDescendants(id);

      setSelected((prev) => {
        const next = new Set(prev);
        const isSelected = next.has(id);

        if (!isSelected) {
          next.add(id);
          for (const d of descendants) next.add(d);

          for (const p of getAncestors(id)) {
            next.add(p);
          }

          return Array.from(next);
        }

        next.delete(id);
        for (const d of descendants) next.delete(d);

        for (const p of getAncestors(id)) {
          const children = index.nodes[p]?.children ?? [];

          const hasSelectedChild = children.some(
            (c) =>
              next.has(c) ||
              getDescendants(c).some((d) => next.has(d))
          );

          if (!hasSelectedChild) {
            next.delete(p);
          }
        }

        return Array.from(next);
      });
    },
    [getDescendants, getAncestors, index.nodes]
  );

  const getSelectionState = useCallback(
    (id: string) => {
      const node = index.nodes[id];
      if (!node) {
        return { checked: false, indeterminate: false };
      }

      const descendants = getDescendants(id);
      const selfSelected = selected.includes(id);

      if (descendants.length === 0) {
        return {
          checked: selfSelected,
          indeterminate: false
        };
      }

      let selectedCount = 0;

      for (const d of descendants) {
        if (selected.includes(d)) {
          selectedCount++;
        }
      }

      const total = descendants.length;

      const allSelected = selectedCount === total && total > 0;
      const someSelected = selectedCount > 0;

      const checked = selfSelected && allSelected;

      const indeterminate = !checked && someSelected;

      return {
        checked,
        indeterminate
      };
    },
    [selected, index, getDescendants]
  );

  const getLeafSelected = useCallback(() => {
    return selected.filter(
      (id) =>
        index.nodes[id] &&
        index.nodes[id].children.length === 0
    );
  }, [selected, index]);

  return useMemo(
    () => ({
      selected,
      toggleNode,
      getSelectionState,
      getLeafSelected
    }),
    [selected, toggleNode, getSelectionState, getLeafSelected]
  );
}